import type {
  CertificateProgramEnrollment,
  CertificateProgramEnrollments,
} from '../client/generated'
import {
  CertificateProgramEnrollmentSource,
  CertificateProgramEnrollmentStatus,
  CertificateProgramTeachingPlatform,
} from '../client/generated'

export type CertificateEnrollmentAccessState =
  | 'active'
  | 'consent_required'
  | 'pending'
  | 'expired'
  | 'none'

/**
 * Maps an enrollment payload to its access state. Key off `status`, never
 * `isEnrolled` — the backend keeps `isEnrolled` false for expired enrollments,
 * so it cannot distinguish expired from never-enrolled. `inactive` (refund /
 * revoke) and a missing status both map to 'none': neither grants access nor
 * any expired-specific treatment. Trials grant no access to Educator-powered
 * programs, so they map to 'none' too.
 *
 * 'consent_required' is an active enrollment whose owning organization requires
 * data-sharing consent the learner has not given. The entitlement is live and
 * its clock is running; only content access is withheld.
 */
export const getCertificateEnrollmentAccessState = (
  enrollment?: Pick<
    CertificateProgramEnrollment,
    'status' | 'dataSharingConsent' | 'source' | 'certificateProgram'
  > | null,
): CertificateEnrollmentAccessState => {
  if (
    enrollment?.source === CertificateProgramEnrollmentSource.TRIAL &&
    enrollment.certificateProgram?.teachingPlatform ===
      CertificateProgramTeachingPlatform.EDUCATOR
  ) {
    return 'none'
  }
  switch (enrollment?.status) {
    case CertificateProgramEnrollmentStatus.ACTIVE:
      return enrollment.dataSharingConsent?.required &&
        !enrollment.dataSharingConsent.satisfied
        ? 'consent_required'
        : 'active'
    case CertificateProgramEnrollmentStatus.PENDING:
      return 'pending'
    case CertificateProgramEnrollmentStatus.EXPIRED:
      return 'expired'
    default:
      return 'none'
  }
}

const enrollmentsForProgram = (
  enrollments: CertificateProgramEnrollments | undefined,
  certificateProgramUuid: string | undefined,
) =>
  certificateProgramUuid
    ? (enrollments?.enrollments ?? []).filter(
        (enrollment) =>
          enrollment.certificateProgram?.uuid === certificateProgramUuid,
      )
    : []

// A consent-blocked learner is still enrolled — their cert must keep
// appearing on the hub, or the re-offer is unreachable.
const hasLiveAccess = (enrollment: CertificateProgramEnrollment) => {
  const accessState = getCertificateEnrollmentAccessState(enrollment)
  return (
    accessState === 'active' ||
    accessState === 'pending' ||
    accessState === 'consent_required'
  )
}

export const isEnrolledInCertificateProgram = (
  enrollments: CertificateProgramEnrollments | undefined,
  certificateProgramUuid: string | undefined,
): boolean => {
  if (!certificateProgramUuid) return false

  // A learner can hold several rows for one program (an expired one plus a
  // re-purchase), so every match must be checked, not just the first.
  return enrollmentsForProgram(enrollments, certificateProgramUuid).some(
    hasLiveAccess,
  )
}

/**
 * Expired enrollments, one row per program: the most recent expired term,
 * omitting programs where the learner holds a live row. The API returns this
 * set as `expiredEnrollments` when asked with expired=true; the fallback
 * derives it from the flat list for a response that predates the field.
 */
export const getExpiredCertificateProgramEnrollments = (
  enrollments: CertificateProgramEnrollments | undefined,
): CertificateProgramEnrollment[] => {
  if (enrollments?.expiredEnrollments) return enrollments.expiredEnrollments

  const all = enrollments?.enrollments ?? []
  const liveProgramUuids = new Set(
    all.filter(hasLiveAccess).map((e) => e.certificateProgram?.uuid),
  )
  const latestByProgram = new Map<string, CertificateProgramEnrollment>()
  all.forEach((enrollment) => {
    const uuid = enrollment.certificateProgram?.uuid
    if (
      !uuid ||
      liveProgramUuids.has(uuid) ||
      getCertificateEnrollmentAccessState(enrollment) !== 'expired'
    ) {
      return
    }
    const previous = latestByProgram.get(uuid)
    if (
      !previous ||
      (enrollment.expiresAt ?? '') > (previous.expiresAt ?? '')
    ) {
      latestByProgram.set(uuid, enrollment)
    }
  })
  return [...latestByProgram.values()]
}

/**
 * The start date of the cohort a learner is waiting on: the `cohortStartDate`
 * of their pending enrollment in the program. Undefined for a pending
 * enrollment with no cohort and for every other access state.
 */
export const getPendingCohortStartDate = (
  enrollments: CertificateProgramEnrollments | undefined,
  certificateProgramUuid: string | undefined,
): string | undefined =>
  enrollmentsForProgram(enrollments, certificateProgramUuid).find(
    (enrollment) =>
      getCertificateEnrollmentAccessState(enrollment) === 'pending',
  )?.cohortStartDate

/**
 * True when the learner's only access to the program has lapsed: at least one
 * expired row and no active or pending one. A re-purchase after expiry makes
 * this false again.
 */
export const hasExpiredCertificateProgramAccess = (
  enrollments: CertificateProgramEnrollments | undefined,
  certificateProgramUuid: string | undefined,
): boolean => {
  if (!certificateProgramUuid) return false
  if (isEnrolledInCertificateProgram(enrollments, certificateProgramUuid)) {
    return false
  }
  return getExpiredCertificateProgramEnrollments(enrollments).some(
    (enrollment) =>
      enrollment.certificateProgram?.uuid === certificateProgramUuid,
  )
}
